// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How To Use
 * 1. add this line to CustomFieldDefinition.ts
 *    export interface PaddingTemplateDefinition extends BaseFieldDefinition<"PaddingField", Padding> {}
 * 2. Add Field to your widget
 *      {
 *           name: "padding",
 *           displayName: "Padding",
 *           editorTemplate: "PaddingField",
 *           defaultValue: {},
 *           fieldType: "General",
 *      },
 * 3. In your CSS use this function:  generatePaddingStyles(zoneOptions.padding)
 *    and it will handle fetch padding data in your code
 */

/* eslint-disable spire/fenced-imports */
import translate from "@insite/client-framework/Translate";
import { ButtonIcon } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import TextField from "@insite/mobius/TextField";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import { TS } from "@insite/shell/TS";
import {
    CustomEditorTemplateProps,
    Padding,
    PaddingProperties,
    PaddingTemplateDefinition,
} from "@vanmeter/CustomFieldDefinition";
import { xScreen } from "@vanmeter/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import styled, { css } from "styled-components";

interface State {
    screen: keyof Padding;
}

type Props = CustomEditorTemplateProps<Padding, PaddingTemplateDefinition>;

class PaddingField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            screen: "xl",
        };
    }

    onChange = (event: React.FormEvent<HTMLInputElement>, position: keyof PaddingProperties) => {
        const value = event.currentTarget.value;

        const {
            fieldValue,
            updateField,
            fieldDefinition: { name },
        } = this.props;
        const { screen } = this.state;

        const newFieldValue = cloneDeep(fieldValue);
        if (!newFieldValue[screen]) {
            newFieldValue[screen] = {};
        }
        newFieldValue[screen][position] = value;
        updateField(name, newFieldValue);
    };

    validateCssLength = (value: string) => {
        if (value === "" || value === "0") {
            return true;
        }
        const lengthRegex = /[\d]+([a-zA-Z]+|%)/gi;
        if (!lengthRegex.test(value)) {
            return false;
        }
        return true;
    };

    render() {
        const { item, fieldDefinition, fieldValue } = this.props;
        const { screen } = this.state;

        fieldDefinition.validate = (value: any) => {
            if (value && value[screen]) {
                for (const property of Object.keys(value[screen])) {
                    if (!this.validateCssLength(value[screen][property])) {
                        return TS("Value must use a valid CSS value format (e.g. 10px or 30%)");
                    }
                }
            }
            return null;
        };
        const field = (property: keyof PaddingProperties, placeholder: string) => (
            <TextField
                name={property}
                className={property}
                placeholder={placeholder}
                onChange={(event: any) => this.onChange(event, property)}
                value={fieldValue?.[screen]?.[property] || ""}
                sizeVariant="small"
            />
        );

        return (
            <StandardControl fieldDefinition={fieldDefinition as any}>
                <ActionsWrapper>
                    <ButtonIcon
                        src="Smartphone"
                        size={20}
                        className={`${screen === "sm" ? "active" : ""}`}
                        onClick={() => this.setState({ screen: "sm" })}
                        aria-describedby={translate("Phone")}
                    />
                    <ButtonIcon
                        src="Tablet"
                        size={20}
                        className={`${screen === "md" ? "active" : ""}`}
                        onClick={() => this.setState({ screen: "md" })}
                        aria-describedby={translate("Tablet")}
                    />
                    <ButtonIcon
                        src="Monitor"
                        size={20}
                        className={`${screen === "lg" ? "active" : ""}`}
                        onClick={() => this.setState({ screen: "lg" })}
                        aria-describedby={translate("Desktop")}
                    />
                    <ButtonIcon
                        src="Monitor"
                        width={30}
                        height={20}
                        className={`${screen === "xl" ? "active" : ""}`}
                        onClick={() => this.setState({ screen: "xl" })}
                        aria-describedby={translate("Wide Desktop")}
                    />
                </ActionsWrapper>
                <Wrapper>
                    {field("padding-top", "top")}
                    {field("padding-right", "right")}
                    {field("padding-bottom", "bottom")}
                    {field("padding-left", "left")}
                </Wrapper>
            </StandardControl>
        );
    }
}

export default PaddingField;

const Wrapper = styled.div`
    border: 1px solid #000000;
    margin: 35px;
    padding: 35px;
    position: relative;
    height: 120px;
    ${FormFieldStyle} {
        position: absolute;
        margin: 0;
        width: 60px;

        &:nth-of-type(1) {
            left: 50%;
            transform: translateX(-50%);
            top: -15px;
        }

        &:nth-of-type(2) {
            top: 50%;
            transform: translateY(-50%);
            right: -30px;
        }

        &:nth-of-type(3) {
            left: 50%;
            transform: translateX(-50%);
            bottom: -15px;
        }

        &:nth-of-type(4) {
            top: 50%;
            transform: translateY(-50%);
            left: -30px;
        }
        input {
            text-align: center;

            &::placeholder {
                font-size: 12px;
            }
        }
    }
`;

const ActionsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
    ${ButtonIcon} {
        height: 30px;
        width: 30px;
        justify-content: center;
        cursor: pointer;
        &:hover,
        &.active {
            background-color: #e5ebff;
        }
    }
`;

export const generatePaddingStyles = (padding: any) => {
    return css`
        ${Object.keys(padding)
            .sort()
            .map(size => {
                const paddingStyles = Object.keys(padding[size])
                    .map(property => {
                        return `${property}: ${padding[size][property]};`;
                    })
                    .join("\n");

                if (size !== "xl") {
                    const mediaQuery = xScreen("lte", size);
                    return css`
                        ${mediaQuery} {
                            ${paddingStyles}
                        }
                    `;
                }
                return css`
                    ${paddingStyles}
                `;
            })}
    `;
};
